import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { events } from '../../lib/api';

function HomePage() {
  const [eventList, setEventList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadEvents(); }, []);

  const loadEvents = async () => {
    try {
      const res = await events.getUpcoming();
      setEventList(res.data.events || []);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  };

  const liveEvents = eventList.filter(e => e.status === 'live');
  const upcomingEvents = eventList.filter(e => e.status === 'upcoming');

  return (
    <div>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 py-24 text-center">
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
          Immersive Live Events
        </h1>
        <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto mb-10">
          Experience live performances in spatial video on Apple Vision Pro.
          Feel like you're right there on stage.
        </p>
        {liveEvents.length > 0 && (
          <Link
            to={`/event/${liveEvents[0].id}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-semibold text-lg hover:bg-red-700 transition-colors"
          >
            <span className="w-3 h-3 rounded-full bg-white animate-pulse" />
            Watch Live Now
          </Link>
        )}
      </section>

      {/* Live Now */}
      {liveEvents.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 mb-16">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-red-500 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Live Now
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {liveEvents.map(evt => (
              <EventCard key={evt.id} event={evt} live />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming */}
      <section className="max-w-6xl mx-auto px-4 mb-16">
        <h2 className="text-2xl font-bold mb-6">Upcoming Events</h2>
        {loading ? (
          <p className="text-neutral-500">Loading...</p>
        ) : upcomingEvents.length === 0 && liveEvents.length === 0 ? (
          <p className="text-neutral-500">No upcoming events. Check back soon.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {upcomingEvents.map(evt => (
              <EventCard key={evt.id} event={evt} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function EventCard({ event: evt, live = false }) {
  return (
    <Link
      to={`/event/${evt.id}`}
      className="group block rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 overflow-hidden hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
    >
      <div className="aspect-video bg-neutral-200 dark:bg-neutral-800 relative">
        {live && (
          <span className="absolute top-3 left-3 px-2 py-1 text-xs font-bold uppercase tracking-wider bg-red-600 text-white rounded">
            Live
          </span>
        )}
        {evt.streams?.length > 0 && (
          <span className="absolute top-3 right-3 px-2 py-1 text-xs bg-black/50 text-white rounded">
            {evt.streams.length} camera{evt.streams.length > 1 ? 's' : ''}
          </span>
        )}
        <div className="absolute inset-0 flex items-center justify-center text-4xl opacity-30">
          🎵
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-lg group-hover:text-blue-500 transition-colors">
          {evt.title}
        </h3>
        {evt.description && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 line-clamp-2">
            {evt.description}
          </p>
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-neutral-500">
          {evt.scheduledStart && (
            <span>{new Date(evt.scheduledStart).toLocaleString('da-DK', {
              weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
            })}</span>
          )}
          {evt.location && <span>{evt.location}</span>}
          {evt.category && (
            <span className="px-2 py-0.5 rounded-full bg-neutral-200 dark:bg-neutral-800">{evt.category}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default HomePage;
